import { useCallback, useSyncExternalStore } from 'react';

import { DEFAULT_ARTICLE_FONT_SIZE } from '@/lib/article-renderer/core/font-scale';
import { FONT_SIZE_KEY } from '@/constants';

/**
 * The reader's text-size setting, in % of the body font (the article root's
 * `font-size`, see core/font-scale.ts). The range runs from half to double
 * the default: WCAG 1.4.4 needs text resizable to 200%, and since native
 * pinch-zoom is disabled on purpose (improvements/DASHBOARD.md Section 18)
 * this control is how article text gets there.
 *
 * Steps are 12.5% of the default. They are exact binary fractions, so
 * stepping never drifts and the `=== MIN/MAX` checks that disable the
 * buttons hold.
 */
export const DEFAULT_FONT_SIZE = DEFAULT_ARTICLE_FONT_SIZE;
export const FONT_SIZE_STEP = DEFAULT_FONT_SIZE / 8;
export const MIN_FONT_SIZE = DEFAULT_FONT_SIZE / 2;
export const MAX_FONT_SIZE = DEFAULT_FONT_SIZE * 2;

/**
 * Clamps to the range and snaps to a step. Values saved before the range
 * changed (the old 30–100 range, stepped by 10 from 62.5) land on the
 * nearest valid step instead of leaving the buttons stuck between steps.
 */
export function normalizeFontSize(value: number): number {
	if (!Number.isFinite(value)) {
		return DEFAULT_FONT_SIZE;
	}
	const steps = Math.round((value - DEFAULT_FONT_SIZE) / FONT_SIZE_STEP);
	const snapped = DEFAULT_FONT_SIZE + steps * FONT_SIZE_STEP;
	return Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, snapped));
}

const listeners = new Set<() => void>();

function readStoredFontSize(): number {
	try {
		const stored = localStorage.getItem(FONT_SIZE_KEY);
		return stored ? normalizeFontSize(parseFloat(stored)) : DEFAULT_FONT_SIZE;
	} catch {
		return DEFAULT_FONT_SIZE;
	}
}

// In memory, written through to localStorage: a failed write (private mode)
// still changes the size for this session.
let currentFontSize: number | undefined;

function getFontSize(): number {
	currentFontSize ??= readStoredFontSize();
	return currentFontSize;
}

function subscribe(listener: () => void) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

/**
 * The saved text size and its setter. A module-level store (same shape as
 * `useLineHeight`), not per-caller state: the reader menu's A-down/A-up
 * buttons and the Settings dialog both change it, and an open reader has to
 * follow whichever one did.
 */
export function useFont(): FontResponse {
	const fontSize = useSyncExternalStore(
		subscribe,
		getFontSize,
		() => DEFAULT_FONT_SIZE,
	);
	// Stable: callers list it in `useMemo` deps.
	const setFontSize = useCallback((size: number) => {
		currentFontSize = normalizeFontSize(size);
		try {
			localStorage.setItem(FONT_SIZE_KEY, `${currentFontSize}`);
		} catch {
			// Private mode etc.: still applies for this session.
		}
		listeners.forEach((listener) => listener());
	}, []);
	return { fontSize, setFontSize };
}

interface FontResponse {
	fontSize: number;
	setFontSize: (size: number) => void;
}
